/**
 * Normalize registration/offer extraMerchandise to line items.
 * Accepts legacy string IDs (quantity 1) and `{ merchandiseId, quantity, variantId }` objects.
 *
 * @param {unknown} value
 * @returns {Array<{ merchandiseId: string, quantity: number, variantId?: string }>}
 */
export function normalizeExtraMerchandise(value) {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    if (item && typeof item === "object" && item.merchandiseId != null) {
      const line = { merchandiseId: String(item.merchandiseId), quantity: Number(item.quantity) || 1 };
      const variantId = typeof item.variantId === "string" ? item.variantId.trim() : "";
      if (variantId) line.variantId = variantId;
      return line;
    }
    return { merchandiseId: item?.toString?.() ?? String(item), quantity: 1 };
  });
}

/**
 * Compare merchandise ids that may be strings or ObjectId-like.
 *
 * @param {unknown} a
 * @param {unknown} b
 * @returns {boolean}
 */
export function merchandiseIdEquals(a, b) {
  return (a?.toString?.() ?? String(a)) === (b?.toString?.() ?? String(b));
}

/**
 * Labels of the catalog item's variant options.
 *
 * @param {{ variantOptions?: Array<{ label?: string }> }} [merch]
 * @returns {string[]}
 */
export function merchandiseVariantLabels(merch) {
  if (!Array.isArray(merch?.variantOptions)) return [];
  return merch.variantOptions.map((option) => String(option?.label ?? "").trim()).filter(Boolean);
}

/**
 * Ids of the catalog item's variant options.
 *
 * @param {{ variantOptions?: Array<{ variantId?: string }> }} [merch]
 * @returns {string[]}
 */
export function merchandiseVariantIds(merch) {
  return (merch?.variantOptions || []).map((option) => String(option?.variantId ?? "")).filter(Boolean);
}

/**
 * True when the catalog item requires a variant choice.
 *
 * @param {{ variantOptions?: Array<{ label?: string }> }} [merch]
 * @returns {boolean}
 */
export function merchandiseHasVariants(merch) {
  return merchandiseVariantLabels(merch).length > 0;
}

/**
 * The catalog option whose variantId matches the chosen id.
 *
 * @param {{ variantOptions?: Array<{ variantId?: string }> }} [merch]
 * @param {string} [variantId]
 * @returns {object|undefined}
 */
export function findVariantOption(merch, variantId) {
  const id = String(variantId ?? "").trim();
  if (!id) return undefined;
  return (merch?.variantOptions || []).find((option) => String(option?.variantId ?? "").trim() === id);
}

/**
 * True when this option has no pieces left. Runners see `soldOut`; editors see a remaining count of 0.
 *
 * @param {{ soldOut?: boolean, maxAmount?: number|null, bookedCount?: number }} [option]
 * @returns {boolean}
 */
export function variantOptionSoldOut(option) {
  if (!option) return false;
  if (option.soldOut) return true;
  if (option.remaining === 0) return true;
  return remainingFromMerch(option) === 0;
}

/**
 * True when every variant of the item is sold out. An item with no variants is not.
 *
 * @param {object} [merch]
 * @returns {boolean}
 */
export function allVariantsSoldOut(merch) {
  if (!merchandiseHasVariants(merch)) return false;
  return merch.variantOptions.every((option) => variantOptionSoldOut(option));
}

/**
 * Display name for a selected line, including the chosen variant.
 *
 * @param {string} name
 * @param {string} [variant]
 * @returns {string}
 */
export function merchandiseLineLabel(name, variant) {
  return variant ? `${name} (${variant})` : name;
}

/**
 * True when this registration may select a captain-only item.
 * A solo registration may. On a team, only the captain may.
 *
 * @param {{ teamId?: unknown }} [registration]
 * @param {boolean} [isTeamCaptain]
 * @returns {boolean}
 */
export function canSelectCaptainMerchandise(registration, isTeamCaptain = false) {
  return !registration?.teamId || Boolean(isTeamCaptain);
}

/**
 * Split catalog rows this person can see into mandatory and optional lists.
 * Optional captain-only items are omitted unless `canSelectCaptainMerchandise` is true.
 * A mandatory captain-only item stays visible to a team member when `lockedCaptainView` is set.
 *
 * @param {Array<Object>} merchandises
 * @param {boolean} [maySelect] - True for the team captain and for a solo registration
 * @param {boolean} [lockedCaptainView]
 * @returns {{ mandatory: Array<Object>, optional: Array<Object> }}
 */
export function partitionVisibleMerchandise(merchandises, maySelect = false, lockedCaptainView = false) {
  const visible = (merchandises || []).filter((merch) => {
    if (maySelect || !merch.captainExclusive) return true;
    return lockedCaptainView && merch.mandatory;
  });
  return {
    mandatory: visible.filter((merch) => merch.mandatory),
    optional: visible.filter((merch) => !merch.mandatory),
  };
}

/**
 * True when a team member may see this captain-only item but must not change it.
 *
 * @param {{ captainExclusive?: boolean, mandatory?: boolean }} [merch]
 * @param {boolean} [lockedCaptainView]
 * @returns {boolean}
 */
export function isLockedCaptainMerchandise(merch, lockedCaptainView = false) {
  return Boolean(lockedCaptainView && merch?.captainExclusive && merch?.mandatory);
}

/**
 * Remaining pieces for a capped catalog item. `null` means unlimited.
 *
 * @param {{ maxAmount?: number|null, bookedCount?: number }} merch
 * @returns {number|null}
 */
export function remainingFromMerch(merch) {
  if (merch?.maxAmount == null) return null;
  return Math.max(0, merch.maxAmount - (merch.bookedCount || 0));
}

/**
 * Remaining pieces for a variant: its own cap, or the tightest linked pool.
 * `null` means unlimited.
 *
 * @param {{ stockPools?: Array<{ poolId?: string, maxAmount?: number|null, bookedCount?: number }> }} [merch]
 * @param {{ maxAmount?: number|null, bookedCount?: number, poolIds?: string[] }} [option]
 * @returns {number|null}
 */
export function variantRemaining(merch, option) {
  if (!option) return null;
  if (!option.poolIds?.length) {
    if (option.maxAmount == null) return null;
    return Math.max(0, option.maxAmount - (option.bookedCount || 0));
  }
  const remainings = option.poolIds
    .map((id) => (merch.stockPools || []).find((pool) => pool.poolId === id))
    .filter((pool) => pool && pool.maxAmount != null)
    .map((pool) => Math.max(0, pool.maxAmount - (pool.bookedCount || 0)));
  if (remainings.length === 0) return null;
  return Math.min(...remainings);
}

/**
 * Unit price of the chosen variant when it has a price, otherwise the item price.
 *
 * @param {{ price?: number|null, variantOptions?: object[] }} [merch]
 * @param {string} [variantId]
 * @returns {number|null}
 */
/**
 * Price shown before a variant is chosen: the item price, or the lowest variant price.
 * `0` is a price. An empty result means there is nothing to show.
 *
 * @param {{ price?: number|null, variantOptions?: Array<{ price?: number|null }> }} [merch]
 * @returns {number|null}
 */
export function catalogListPrice(merch) {
  if (merch?.price != null && merch.price !== "") return Number(merch.price);
  const prices = (merch?.variantOptions || [])
    .map((option) => option?.price)
    .filter((price) => price != null && price !== "");
  if (prices.length === 0) return null;
  return Math.min(...prices);
}

export function applicableUnitPrice(merch, variantId) {
  const option = findVariantOption(merch, variantId);
  if (option?.price != null) return Number(option.price);
  if (merch?.price != null) return Number(merch.price);
  return null;
}

/**
 * Find a hydrated catalog row on a race by merchandise id.
 *
 * @param {{ merchandises?: Array<{ _id?: unknown }> }} race
 * @param {unknown} merchandiseId
 * @returns {object|undefined}
 */
export function findRaceMerchandise(race, merchandiseId) {
  return (race?.merchandises || []).find((m) => merchandiseIdEquals(m._id, merchandiseId));
}

/**
 * Catalog unit price. A VIP voucher discounts mandatory items by its percent. Optional items stay full price.
 *
 * @param {{ price?: number, mandatory?: boolean }} [merch]
 * @param {{ vip?: boolean, discount?: number }} [voucher]
 * @param {string} [variantId]
 * @returns {number}
 */
export function merchandiseUnitPrice(merch, voucher, variantId) {
  const price = applicableUnitPrice(merch, variantId);
  if (price == null) return 0;
  const discount = Number(voucher?.discount) || 0;
  if (voucher?.vip && merch?.mandatory && discount > 0) return price * (1 - discount / 100);
  return price;
}

/**
 * Sum selected extra merchandise (quantity × price, VIP discount on mandatory items).
 *
 * @param {object} race
 * @param {unknown} extraMerchandise
 * @param {{ vip?: boolean, discount?: number }} [voucher]
 * @returns {number}
 */
export function sumExtraMerchandisePrices(race, extraMerchandise, voucher) {
  return normalizeExtraMerchandise(extraMerchandise).reduce((sum, { merchandiseId, quantity, variantId }) => {
    const merch = findRaceMerchandise(race, merchandiseId);
    return sum + merchandiseUnitPrice(merch, voucher, variantId) * quantity;
  }, 0);
}

/**
 * Upper bound for a bulk quantity field. Unlimited stock returns null.
 * When the current line is already selected, remaining may exclude it — keep at least that qty.
 *
 * @param {object} merch
 * @param {number} [selectedQuantity]
 * @param {{ ignoreStock?: boolean }} [options]
 * @returns {number|null}
 */
export function maxSelectableQuantity(merch, selectedQuantity = 0, { ignoreStock = false } = {}) {
  if (ignoreStock) return null;
  const remaining = typeof merch?.remaining === "number" ? merch.remaining : remainingFromMerch(merch);
  if (remaining == null) return null;
  return Math.max(remaining, Number(selectedQuantity) || 0);
}
